import React, { useEffect, useState, useMemo, useRef } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import { FormAutoComplete } from "../../../components/form/FormAutoComplete";
import { useOrganizations } from "../../../services/fhir/organization/useOrganizations";
import { useInsurancePlans } from "../../../services/fhir/insuranceplan/useInsurancePlans";
import { useValueSet } from "../../../hooks/useValueSet";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { FormTextInput } from "../../../components/form/FormTextInput";
import { useFormatter } from "@/utils/useFormatter";
import { ProcedureCard } from "./ProcedureCard";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { PutOrganizationAffiliation } from "@/services/fhir/organizationaffiliation/PutOrganizationAffiliation";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { Identifier, InsurancePlan } from "fhir/r5";
import { useFhirData } from "@/utils/useFhirData";
import { EditOrganization } from "@/services/fhir";
import { RequiredField } from "@/pages/schedule/components/RequiredField";

// Tipo simplificado para OrganizationAffiliation

// Interface para o formulário
interface ProcedureWithValue {
  code: string;
  display: string;
  label: string;
  value: string;
  serviceCost?: number;
}
export interface AffiliationFormData {
  organization: { value: string; label: string } | null;
  insurancePlans: { value: string; label: string; display?: string } | null;
  cnes?: string; // Campo para CNES
  institutionRegistration?: string; // Campo para cadastro na instituição
  procedures?: ProcedureWithValue[] | null;
  currentProcedures?: ProcedureWithValue | null;
  serviceCost?: number;
}

interface AffiliationModalProps {
  open: boolean;
  isEditing: boolean;
  initialData?: Partial<AffiliationFormData> & { id?: string };
  onClose: () => void;
  handleRefetch: () => void;
}

/**
 * Modal para criação e edição de afiliações organizacionais
 *
 * Estrutura do formulário organizada em 3 seções intuitivas:
 * 1. 📋 Identificação: CNES e Cadastro na Instituição
 * 2. ⚙️ Configuração: Organização e Planos de Saúde
 * 3. 🏥 Procedimentos: Seleção de procedimentos e valores
 */
export function AffiliationModal({
  open,
  isEditing,
  initialData,
  onClose,
  handleRefetch,
}: AffiliationModalProps) {
  const { organization, refetchOrganization } = useOrganizationContext();
  // Estado para controlar loading
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para busca de organizações
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState("");

  const cnesValue = useFhirData.findIdentifier(
    organization?.identifier,
    "cnes"
  );

  // Hook para buscar organizações
  const {
    organizations,
    loading: organizationsLoading,
    fetchOrganizations,
  } = useOrganizations();

  // Hook para buscar planos de saúde
  const {
    insurancePlans,
    loading: insurancePlansLoading,
    fetchInsurancePlans,
    clearInsurancePlans,
  } = useInsurancePlans();

  // Hook para buscar procedimentos (ValueSet)
  const [procedureSearchTerm, setProcedureSearchTerm] = useState("");
  const { options: procedureOptions, loading: proceduresLoading } = useValueSet(
    {
      valueSet: "Fluxmed - Procedimentos e eventos em saúde", // ajuste conforme seu ValueSet
      // version: "r5",
      searchTerm: procedureSearchTerm,
      customUrl: "https://fhir.ans.gov.br/ValueSet/procedimento", // ajuste se necessário
    }
  );

  // Configuração do React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    unregister,
    formState: { errors },
  } = useForm<AffiliationFormData>({
    defaultValues: (initialData && {
      ...initialData,
      cnes: cnesValue,
    }) || {
      organization: null,
      insurancePlans: null,
      cnes: cnesValue,
      institutionRegistration: "",
      procedures: [],
    },
  });

  // Remover estados temporários

  const [inputValue, setInputValue] = useState("");
  const autoCompleteRef = useRef<HTMLInputElement>(null);

  const proceduresList =
    useWatch({
      control,
      name: "procedures",
    }) || [];

  const currentProcedure = useWatch({
    control,
    name: "currentProcedures",
  });

  const serviceCost = useWatch({
    control,
    name: "serviceCost",
  });

  const handleAddProcedure = () => {
    const displayValue = currentProcedure?.display || inputValue;

    if (displayValue) {
      // Atualiza os valores no contexto com o novo padrão de nomeação
      const baseFieldName = `procedures.${proceduresList?.length || 0}`;

      setValue(`${baseFieldName}` as any, {
        ...currentProcedure,
        serviceCost,
      });

      setValue("currentProcedures", null);
      setValue("serviceCost", undefined);

      setInputValue("");
      setProcedureSearchTerm("");

      if (autoCompleteRef.current) {
        const input = autoCompleteRef.current.querySelector(
          "input"
        ) as HTMLInputElement;
        if (input) {
          input.value = "";
        }
      }
    }
  };

  const handleRemoveProcedure = (index: number) => {
    // Remove do contexto
    const baseFieldName = `procedures.${index}`;
    setValue(`${baseFieldName}` as any, undefined);
    unregister(`${baseFieldName}` as any);
  };

  const handleChipClick = (procedure: ProcedureWithValue, index: number) => {
    setValue("currentProcedures", procedure, {
      shouldValidate: true,
    });

    setValue("serviceCost", procedure?.serviceCost);

    // Atualiza os estados temporários com os valores existentes

    setInputValue(procedure?.display);
    setProcedureSearchTerm("");

    if (autoCompleteRef.current) {
      const input = autoCompleteRef.current.querySelector(
        "input"
      ) as HTMLInputElement;
      if (input) {
        input.value = procedure?.display;
      }
    }

    handleRemoveProcedure(index);
  };

  // Handler para submeter o formulário
  const onSubmit = async (data: AffiliationFormData) => {
    setIsSubmitting(true);

    try {
      const references = {
        prestadorId: organization?.id || "",
        prestadorDisplay: organization?.name || "",
        defaultId: initialData?.id || undefined,
      };

      await PutOrganizationAffiliation(data, references);

      if (data?.cnes?.length && data?.cnes !== cnesValue) {
        await EditOrganization(organization, "cnes", data.cnes);

        refetchOrganization();
      }

      // Mostra mensagem de sucesso
      toast.success("Afiliação salva com sucesso");

      // Fecha o modal
      handleRefetch();
      onClose();
    } catch (error) {
      // Mostra mensagem de erro
      console.log(error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao salvar afiliação"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Observa o valor do campo organização para controlar habilitação dos planos
  const selectedOrganization = watch("organization");

  // Buscar organizações quando o modal abrir
  useEffect(() => {
    if (open) {
      fetchOrganizations({
        active: true,
        count: 50,
        type: "pay",
        content: organizationSearchTerm || initialData?.organization?.label,
      });

      setProcedureSearchTerm("");
    }
  }, [open, organizationSearchTerm, fetchOrganizations]);

  useEffect(() => {
    if (open) {
      setProcedureSearchTerm("");
    }
  }, [open]);

  // Buscar planos de saúde ao selecionar organização
  useEffect(() => {
    if (selectedOrganization?.value) {
      fetchInsurancePlans({
        ownedBy: selectedOrganization.value || initialData?.organization?.value,
        count: 150,
      });
      // Limpa os planos selecionados ao trocar de organização

      if (!initialData?.insurancePlans?.value) {
        reset({
          ...watch(),
          insurancePlans: null,
        });
      }
    } else {
      clearInsurancePlans();
      reset({
        ...watch(),
        insurancePlans: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization?.value]);

  // Converter organizações para o formato do FormAutoComplete
  const organizationOptions = useMemo(() => {
    return organizations.map((org) => ({
      value: org.id || "",
      label: org.name || org.id || "",
    }));
  }, [organizations]);

  // Converter planos para o formato do autocomplete, filtrando apenas ativos e exibindo apenas o nome
  const insurancePlanOptions = useMemo(() => {
    return insurancePlans.map((plan: any) => {
      const auxId = plan?.identifier?.find(
        (id: Identifier) => id.use === "official" || id.use === "secondary"
      );
      return {
        value: plan.id || "",
        label: `${plan.name} - ${auxId?.value}`,
        display: plan.name || "",
      };
    });
  }, [insurancePlans]);

  // Converter opções de procedimentos para o formato aceito pelo FormAutoComplete
  const mappedProcedureOptions = useMemo(() => {
    return procedureOptions.map((opt) => ({
      value: String(opt?.value) || "",
      label: `${opt?.display} - ${opt?.code}`,
      code: opt?.code || "",
      display: opt?.display || opt?.label || String(opt?.value || "") || "",
    }));
  }, [procedureOptions]);

  const clearProcedureField = () => {
    setValue("currentProcedures", null);
    setValue("serviceCost", undefined);
    setInputValue("");
    setProcedureSearchTerm("");
  };

  useEffect(() => {
    if (!open) {
      clearProcedureField();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {isEditing ? "Editar Plano de Saúde" : "Novo Plano de Saúde"}
      </DialogTitle>

      <DialogContent sx={{ minHeight: "450px" }}>
        {!cnesValue && (
          <>
            <Typography
              variant="subtitle2"
              color="neutral600.main"
              fontWeight="600"
            >
              Informe o CNES da sua organização para emissão de guias TISS.{" "}
              <RequiredField />
            </Typography>
            <FormTextInput
              control={control}
              label="CNES"
              name="cnes"
              placeholder="Ex: 1234567"
            />
          </>
        )}

        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          {/* Seção 1: Identificação */}

          {/* Seção 2: Configuração */}
          <Box>
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              Configuração
            </Typography>
            <Typography color="neutral600.main" fontWeight="300" mb={2}>
              Selecione a organização e o plano de saúde para a afiliação.
            </Typography>
            <Stack direction="column" spacing={2}>
              <Stack direction="row" spacing={2}>
                <FormAutoComplete
                  noOptionsText="Nenhuma organização encontrada"
                  name="organization"
                  control={control}
                  disabled={isEditing}
                  label="Organização"
                  required={true}
                  loading={organizationsLoading}
                  selectOptions={organizationOptions}
                  onInputChange={(_, value) => {
                    setOrganizationSearchTerm(value);
                  }}
                  auxOnChange={(selectedOrg: any) => {
                    // Limpa planos quando organização muda
                    if (selectedOrg?.value !== selectedOrganization?.value) {
                      reset({
                        ...watch(),
                        organization: selectedOrg,
                        insurancePlans: null,
                      });
                    }
                  }}
                />

                <FormTextInput
                  control={control}
                  label="Cadastro na Instituição"
                  name="institutionRegistration"
                  placeholder="Ex: CAD123456"
                />
              </Stack>

              <FormAutoComplete
                name="insurancePlans"
                control={control}
                noOptionsText="Nenhum plano encontrado"
                disabled={
                  isEditing ||
                  !selectedOrganization?.value ||
                  !!initialData?.organization?.value
                }
                label="Plano"
                required={true}
                selectOptions={insurancePlanOptions}
                loading={insurancePlansLoading}
              />
            </Stack>
          </Box>

          {/* Seção 3: Procedimentos */}
          <Box>
            <Typography variant="h6" color="primary.main" fontWeight="bold">
              Procedimentos
            </Typography>
            <Typography color="neutral600.main" fontWeight="300" mb={2}>
              Selecione o procedimento e o valor para cada procedimento.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <FormAutoComplete
                key={`procedures-${proceduresList?.length || 0}-${
                  procedureSearchTerm ? "searching" : "default"
                }`}
                name={`currentProcedures`}
                control={control}
                noOptionsText="Nenhum procedimento encontrado"
                onInputChange={(_, value) => {
                  setProcedureSearchTerm(value);
                }}
                label="Procedimento"
                selectOptions={mappedProcedureOptions}
                loading={proceduresLoading}
              />

              <FormTextInput
                control={control}
                key={`procedures.${proceduresList?.length || 0}`}
                label="Valor (R$)"
                name={`serviceCost`}
                placeholder="Ex: R$ 150,00"
                mask={useFormatter.formatCurrency}
              />
              <Button
                sx={{ minWidth: "100px" }}
                variant="outlined"
                disabled={!currentProcedure || !serviceCost}
                onClick={handleAddProcedure}
              >
                Adicionar
              </Button>
            </Stack>
          </Box>
          {/* Lista de procedimentos adicionados como cards OU instrução */}
          {proceduresList?.length > 0 ? (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" mb={2}>
                Procedimentos adicionados ({proceduresList.length})
              </Typography>
              <Grid container spacing={2} justifyContent="flex-start">
                {proceduresList?.map((item, idx) => (
                  <Grid
                    item
                    xs={12}
                    sm={8}
                    md={6}
                    lg={4}
                    key={`${item?.value}-${idx}`}
                  >
                    <ProcedureCard
                      item={item}
                      index={idx}
                      onEdit={handleChipClick}
                      onDelete={handleRemoveProcedure}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box
              mt={4}
              mb={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={6}
              border="1px solid"
              borderColor="neutral300.main"
              borderRadius="16px"
              sx={{
                background: "linear-gradient(to bottom, #f9f9f9, #ffffff)",
              }}
            >
              <AddCircleOutlineIcon
                color="primary"
                sx={{ fontSize: 64, mb: 2, opacity: 0.7 }}
              />
              <Typography
                variant="h6"
                color="neutral700.main"
                fontWeight="500"
                gutterBottom
              >
                Nenhum procedimento adicionado
              </Typography>
              <Typography
                variant="body1"
                color="neutral700.main"
                align="center"
                sx={{ maxWidth: 400, mb: 2 }}
              >
                Adicione um procedimento usando o formulário acima para começar
                a configurar seus serviços.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          color="primary"
          disabled={isSubmitting || proceduresList?.length === 0}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isSubmitting
            ? "Salvando..."
            : isEditing
            ? "Salvar Alterações"
            : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
